import cn from 'classnames'
import React, {
  Children,
  cloneElement,
  FC,
  memo,
  NamedExoticComponent,
  useEffect,
  useState,
} from 'react'

import { RenderWhen } from '../renderWhen'

import styles from './Carousel.module.scss'

interface CarouselProps {
  children: React.ReactNode
  selectedIndex?: number
  onChange?: (index: number) => void
  hasIndicators?: boolean
  color?: 'default' | 'black' | 'white'
  classes?: {
    carouselContainer?: string
    navButton?: string
    carouselWrapper?: string
    carouselSlide?: string
    active?: string
    carouselIndicators?: string
    indicator?: string
  }
}

interface CarouselItemProps {
  children: React.ReactNode
}

interface CarouselComponent extends NamedExoticComponent<CarouselProps> {
  Item: FC<CarouselItemProps>
}

export const Carousel = memo<CarouselProps>(
  ({ children, selectedIndex = 0, onChange, hasIndicators, classes, color = 'default' }) => {
    const [currentIndex, setCurrentIndex] = useState(selectedIndex)
    const items = Children.toArray(children) as React.ReactElement[]
    const totalItems = items.length

    const goToPrevious = () => {
      setCurrentIndex((prevIndex) => {
        const newIndex = (prevIndex - 1 + totalItems) % totalItems
        onChange && onChange(newIndex)
        return newIndex
      })
    }

    const goToNext = () => {
      setCurrentIndex((prevIndex) => {
        const newIndex = (prevIndex + 1) % totalItems
        onChange && onChange(newIndex)
        return newIndex
      })
    }

    useEffect(() => {
      setCurrentIndex(selectedIndex)
    }, [selectedIndex])

    useEffect(() => {
      return () => {
        setCurrentIndex(0)
      }
    }, [])

    return (
      <div className={cn(styles.carouselContainer, styles[color], classes?.carouselContainer)}>
        <button
          className={cn(styles.navButton, classes?.navButton)}
          hidden={totalItems === 1}
          onClick={goToPrevious}
        >
          <i className="kbase kbase-chevron-left" />
        </button>
        <div className={cn(styles.carouselWrapper, classes?.carouselWrapper)}>
          {items.map((item, index) => (
            <div
              key={index}
              className={`${styles.carouselSlide} ${classes?.carouselSlide} ${index === currentIndex ? styles.active : ''}`}
              style={{
                transform: `translateX(${-currentIndex * 100}%)`,
              }}
            >
              {cloneElement(item, { key: index })}
            </div>
          ))}
        </div>
        <button
          className={cn(styles.navButton, classes?.navButton)}
          hidden={totalItems === 1}
          onClick={goToNext}
        >
          <i className="kbase kbase-chevron-right" />
        </button>

        <RenderWhen condition={hasIndicators}>
          <div
            className={cn(
              styles.carouselIndicators,
              { [styles.hidden]: totalItems === 1 },
              classes?.carouselIndicators
            )}
          >
            {items.map((_, index) => (
              <div
                key={index}
                className={`${styles.indicator} ${classes?.indicator} ${index === currentIndex ? styles.active : ''}`}
                onClick={() => setCurrentIndex(index)}
              ></div>
            ))}
          </div>
        </RenderWhen>
      </div>
    )
  }
) as CarouselComponent

const CarouselItem: FC<CarouselItemProps> = memo(({ children }) => {
  return <>{children}</>
})

Carousel.Item = CarouselItem
